// src/components/CurrentConfigs.js

import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Modal,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import embossedImage from '../assets/mindguard_4.jpg';
import Sidebar from '../components/setupstyles/Sidebar';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

// Styled Components for Consistent Styling
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#040813',
  backgroundImage: 'linear-gradient(145deg, #040813, #081529)',
  color: '#E0E4ED',
  borderRadius: '12px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.6)',
  padding: '16px',
  width: '100%',
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  '&:hover': {
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.9)',
    backgroundImage: 'linear-gradient(145deg, #081529, #0D1F3A)',
  },
}));

const HighlightText = styled(Typography)(({ theme }) => ({
  color: '#9AC0CD', // Softer silver/gray for headings
  fontWeight: 'bold',
  fontFamily: 'Poppins, sans-serif',
}));

const StyledButtonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '8px 0px',
  gap: '10px',
}));

const StyledButton = styled(Button)(({ theme, colorType }) => ({
  backgroundColor: colorType === 'edit' ? '#728C63' : '#D32F2F',
  color: '#E0E4ED',
  borderRadius: '8px',
  padding: '6px 16px',
  textTransform: 'none',
  fontFamily: 'Poppins, sans-serif',
  '&:hover': {
    backgroundColor: colorType === 'edit' ? '#5F7079' : '#C62828',
  },
}));

const EditableTableCell = styled(TableCell)(({ theme }) => ({
  color: '#E0E4ED',
  fontFamily: 'Inter, sans-serif',
}));

const CurrentConfigs = () => {
  const [configs, setConfigs] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [editedConfig, setEditedConfig] = useState({
    channel: '',
    roleConfigurations: [],
  });
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);
  const API_KEY = process.env.REACT_APP_SIMPLEHASH_API_KEY; // If still needed elsewhere
  const DISCORD_API_BASE_URL = '${process.env.REACT_APP_BACKEND_URL}/api/discord'; // Adjust as necessary

  // 1. Delete Configuration
  const handleDeleteConfig = async (configId) => {
    try {
      const configRef = doc(db, 'serverConfigs', configId);
      await deleteDoc(configRef);

      // Update the UI to remove the deleted configuration
      setConfigs((prevConfigs) => prevConfigs.filter((config) => config.guildId !== configId));

      alert('Configuration deleted successfully!');
    } catch (error) {
      console.error('Error deleting configuration:', error);
      alert('Failed to delete configuration. Please try again later.');
    }
  };

  // 2. Edit Configuration
  const handleEditConfig = async (config) => {
    try {
      const channelsResponse = await axios.get(`${DISCORD_API_BASE_URL}/guilds/${config.guildId}/channels`);
      const rolesResponse = await axios.get(`${DISCORD_API_BASE_URL}/guilds/${config.guildId}/roles`);

      if (channelsResponse.status === 200 && rolesResponse.status === 200) {
        setChannels(channelsResponse.data.textChannels);
        setRoles(rolesResponse.data);
      }

      // Include type and combinedRequirement in the edited config
      setCurrentConfig(config);
      setEditedConfig({
        channel: config.channel || '',
        roleConfigurations: Array.isArray(config.roleConfigurations)
          ? config.roleConfigurations.map((rc) => ({
              ...rc,
              requirements: Array.isArray(rc.requirements)
                ? rc.requirements.map((req) => ({
                    ...req,
                    traits: Array.isArray(req.traits) ? req.traits : [],
                  }))
                : [],
            }))
          : [],
      });
      setIsEditModalOpen(true);
    } catch (error) {
      console.error('Error fetching channels or roles for editing:', error);
      alert('Failed to load channels or roles. Please try again later.');
    }
  };

  // 3. Close Edit Modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentConfig(null);
    setEditedConfig({
      channel: '',
      roleConfigurations: [],
    });
  };

  // 4. Handle Role Configuration Changes
  const handleRoleConfigChange = (roleConfigIndex, field, value) => {
    const updatedRoleConfigs = [...editedConfig.roleConfigurations];
    updatedRoleConfigs[roleConfigIndex][field] = value;
    setEditedConfig({ ...editedConfig, roleConfigurations: updatedRoleConfigs });
  };

  // 5. Handle Requirement Changes within a Role Configuration
  const handleRequirementChange = (roleConfigIndex, requirementIndex, field, value) => {
    const updatedRoleConfigs = [...editedConfig.roleConfigurations];
    if (updatedRoleConfigs[roleConfigIndex].type === 'individual') {
      if (!updatedRoleConfigs[roleConfigIndex].requirements) {
        updatedRoleConfigs[roleConfigIndex].requirements = [];
      }
      updatedRoleConfigs[roleConfigIndex].requirements[requirementIndex][field] = value;
    } else if (updatedRoleConfigs[roleConfigIndex].type === 'combined') {
      if (field === 'combinedCount') {
        updatedRoleConfigs[roleConfigIndex].combinedRequirement.combinedCount = Number(value);
      } else if (field === 'collectionAddresses') {
        updatedRoleConfigs[roleConfigIndex].combinedRequirement.collectionAddresses = value;
      }
    }
    setEditedConfig({ ...editedConfig, roleConfigurations: updatedRoleConfigs });
  };

  // 6. Handle Trait Deletion
  const handleDeleteTrait = (roleConfigIndex, requirementIndex, traitIndex) => {
    const updatedRoleConfigs = [...editedConfig.roleConfigurations];
    updatedRoleConfigs[roleConfigIndex].requirements[requirementIndex].traits.splice(traitIndex, 1);
    setEditedConfig({ ...editedConfig, roleConfigurations: updatedRoleConfigs });
  };

  // 7. Handle Adding a New Trait
  const handleAddTrait = (roleConfigIndex, requirementIndex, newTrait) => {
    const updatedRoleConfigs = [...editedConfig.roleConfigurations];
    if (!updatedRoleConfigs[roleConfigIndex].requirements[requirementIndex].traits.includes(newTrait)) {
      updatedRoleConfigs[roleConfigIndex].requirements[requirementIndex].traits.push(newTrait);
      setEditedConfig({ ...editedConfig, roleConfigurations: updatedRoleConfigs });
    }
  };

  // 8. Handle Removing a Role Configuration
  const handleRemoveRoleConfig = (roleConfigIndex) => {
    const updatedRoleConfigs = [...editedConfig.roleConfigurations];
    updatedRoleConfigs.splice(roleConfigIndex, 1);
    setEditedConfig({ ...editedConfig, roleConfigurations: updatedRoleConfigs });
  };

  // 9. Handle Removing a Requirement
  const handleRemoveRequirement = (roleConfigIndex, requirementIndex) => {
    const updatedRoleConfigs = [...editedConfig.roleConfigurations];
    updatedRoleConfigs[roleConfigIndex].requirements.splice(requirementIndex, 1);
    setEditedConfig({ ...editedConfig, roleConfigurations: updatedRoleConfigs });
  };

  // 10. Handle Trait Input (Add New Trait)
  const handleTraitInput = (roleConfigIndex, requirementIndex, event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const newTrait = event.target.value.trim();
      if (newTrait) {
        handleAddTrait(roleConfigIndex, requirementIndex, newTrait);
        event.target.value = '';
      }
    }
  };

  // 11. Handle Saving Edited Configuration
  const handleSaveEditConfig = async () => {
    if (currentConfig) {
      try {
        const configRef = doc(db, 'serverConfigs', currentConfig.guildId);

        // Prepare the roleConfigurations in the format expected by Firestore
        const updatedRoleConfigurations = editedConfig.roleConfigurations.map((rc) => {
          if (rc.type === 'individual') {
            return {
              roleId: rc.roleId,
              type: 'individual',
              requirements: rc.requirements.map((req) => ({
                collectionAddress: req.collectionAddress,
                count: Number(req.nftCount),
                traits: req.traits,
              })),
              combinedRequirement: null,
            };
          } else if (rc.type === 'combined') {
            return {
              roleId: rc.roleId,
              type: 'combined',
              requirements: [],
              combinedRequirement: {
                collectionAddresses: rc.combinedRequirement.collectionAddresses,
                combinedCount: Number(rc.combinedRequirement.combinedCount),
              },
            };
          }
          return rc; // Fallback
        });

        await updateDoc(configRef, {
          channel: editedConfig.channel,
          roleConfigurations: updatedRoleConfigurations,
          updatedAt: new Date(),
        });

        // Update the UI with the edited values
        setConfigs((prevConfigs) =>
          prevConfigs.map((config) =>
            config.guildId === currentConfig.guildId
              ? {
                  ...config,
                  channel: editedConfig.channel,
                  roleConfigurations: editedConfig.roleConfigurations.map((rc) => ({
                    roleId: rc.roleId,
                    roleName: roles.find((role) => role.id === rc.roleId)?.name || 'Unknown Role',
                    type: rc.type,
                    combinedRequirement:
                      rc.type === 'combined'
                        ? {
                            collectionAddresses: rc.combinedRequirement.collectionAddresses,
                            combinedCount: rc.combinedRequirement.combinedCount,
                          }
                        : null,
                    requirements:
                      rc.type === 'individual'
                        ? rc.requirements.map((req) => ({
                            collectionAddress: req.collectionAddress,
                            collectionName:
                              availableCollections.find((col) => col.collectionAddress === req.collectionAddress)?.name ||
                              'Unknown Collection',
                            nftCount: req.nftCount,
                            traits: req.traits,
                          }))
                        : [],
                  })),
                }
              : config
          )
        );

        handleCloseEditModal();
        alert('Configuration updated successfully!');
      } catch (error) {
        console.error('Error updating configuration:', error);
        alert('Failed to update configuration. Please try again later.');
      }
    }
  };

  // Fetch configurations on component mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const discordUserId = localStorage.getItem('discordUserId'); // Assuming the ID is stored in local storage

        if (!discordUserId) {
          console.error('No Discord User ID found');
          return;
        }

        // Reference to the serverConfigs collection, filtered by discordUserId
        const configsRef = collection(db, 'serverConfigs');
        const q = query(configsRef, where('discordUserId', '==', discordUserId));
        const querySnapshot = await getDocs(q);
        const configsData = querySnapshot.docs.map((doc) => ({ ...doc.data(), guildId: doc.id }));

        // Helper function to fetch all channels and find the specific one
        const fetchChannelName = async (guildId, channelId) => {
          try {
            const response = await axios.get(`${DISCORD_API_BASE_URL}/guilds/${guildId}/channels`);
            if (response.status === 200 && response.data && response.data.textChannels) {
              const channel = response.data.textChannels.find((ch) => ch.id === channelId);
              return channel ? channel.name : 'Unknown Channel';
            }
            return 'Unknown Channel';
          } catch (error) {
            console.error(`Error fetching channel name for ${channelId} in guild ${guildId}:`, error);
            return 'Unknown Channel';
          }
        };

        // Helper function to fetch all roles and create a map of roleId to roleName
        const fetchRolesMap = async (guildId) => {
          try {
            const response = await axios.get(`${DISCORD_API_BASE_URL}/guilds/${guildId}/roles`);
            if (response.status === 200 && Array.isArray(response.data)) {
              const rolesMap = {};
              response.data.forEach((role) => {
                rolesMap[role.id] = role.name;
              });
              return rolesMap;
            }
            return {};
          } catch (error) {
            console.error(`Error fetching roles for guild ${guildId}:`, error);
            return {};
          }
        };

        // Process each configuration
        const updatedConfigsPromises = configsData.map(async (config) => {
          const guildName = config.guildName || 'Unknown Guild';
          const channelName = config.channel ? await fetchChannelName(config.guildId, config.channel) : 'Unknown Channel';
          const rolesMap = await fetchRolesMap(config.guildId);

          // Process role configurations by keeping their requirements grouped
          const updatedRoleConfigs = Array.isArray(config.roleConfigurations)
            ? config.roleConfigurations.map((roleConfig) => ({
                roleId: roleConfig.roleId,
                roleName: rolesMap[roleConfig.roleId] || 'Unknown Role',
                type: roleConfig.type || 'individual',
                combinedRequirement: roleConfig.type === 'combined' ? roleConfig.combinedRequirement : null,
                requirements:
                  roleConfig.type === 'individual' && Array.isArray(roleConfig.requirements)
                    ? roleConfig.requirements.map((requirement) => {
                        const collection = Array.isArray(config.collections)
                          ? config.collections.find(
                              (col) => col.collectionAddress === requirement.collectionAddress
                            )
                          : null;
                        return {
                          collectionAddress: requirement.collectionAddress,
                          collectionName: collection ? collection.name : 'Unknown Collection',
                          nftCount: requirement.count || '0',
                          traits: Array.isArray(requirement.traits) ? requirement.traits : [],
                        };
                      })
                    : [],
              }))
            : [];

          return {
            ...config,
            guildName,
            channelName,
            roleConfigurations: updatedRoleConfigs,
          };
        });

        const updatedConfigs = await Promise.all(updatedConfigsPromises);
        setConfigs(updatedConfigs);

        // Extract all unique collections for dropdowns
        const allCollections = [];
        updatedConfigs.forEach((config) => {
          if (Array.isArray(config.collections)) {
            config.collections.forEach((col) => {
              if (!allCollections.find((c) => c.collectionAddress === col.collectionAddress)) {
                allCollections.push(col);
              }
            });
          }
        });
        setAvailableCollections(allCollections);
      } catch (error) {
        console.error('Error fetching configurations:', error);
      }
    };

    fetchConfigs();
  }, [API_KEY, DISCORD_API_BASE_URL]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        backgroundImage: `url(${embossedImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 93%',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Sidebar onSelect={() => {}} />
      <Box
        sx={{
          flexGrow: 1,
          marginLeft: '240px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 4,
        }}
      >
        <Typography variant="h4" align="center" gutterBottom sx={{ color: '#E0E4Ed', fontFamily: 'Poppins, sans-serif' }}>
          Current Configurations
        </Typography>
        <Box
          sx={{
            width: '100%',
            maxWidth: '1200px', // Sets a maximum width for the entire grid for better alignment on large screens
            margin: '0 auto',
          }}
        >
          <Grid container spacing={4} justifyContent="flex-start">
            {configs.length > 0 ? (
              configs.map((config) => (
                <Grid item key={config.guildId} xs={12} sm={6} md={4} lg={6}>
                  <StyledCard>
                    <CardContent>
                      {/* Guild Information */}
                      <HighlightText variant="h6">Guild Name:</HighlightText>
                      <Typography variant="body1" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {config.guildName || config.guildId}
                      </Typography>

                      {/* Channel Information */}
                      <HighlightText variant="h6" sx={{ mt: 2 }}>
                        Channel:
                      </HighlightText>
                      <Typography variant="body1" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {config.channelName || config.channel}
                      </Typography>

                      {/* Subscription Expiry */}
                      <HighlightText variant="h6" sx={{ mt: 2 }}>
                        Subscription Expiry:
                      </HighlightText>
                      <Typography variant="body1" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {config.subscriptionExpiresAt
                          ? new Date(config.subscriptionExpiresAt.seconds * 1000).toLocaleDateString()
                          : 'N/A'}
                      </Typography>

                      {/* Collections Information */}
                      <HighlightText variant="h6" sx={{ mt: 2 }}>
                        Collections:
                      </HighlightText>
                      <TableContainer component={Paper} sx={{ mt: 1, backgroundColor: '#081529' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  color: '#9AC0CD',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontWeight: 'bold',
                                }}
                              >
                                Collection Name
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: '#9AC0CD',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontWeight: 'bold',
                                }}
                              >
                                Collection Address
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: '#9AC0CD',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontWeight: 'bold',
                                }}
                              >
                                Status
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Array.isArray(config.collections) &&
                              config.collections.map((collection, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                      {collection.name || 'Unknown Collection'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                      {collection.collectionAddress}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: collection.isActive ? '#728C63' : '#D32F2F',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {collection.isActive ? 'Active' : 'Inactive'}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Role Configurations */}
                      <HighlightText variant="h6" sx={{ mt: 2 }}>
                        Role Configurations:
                      </HighlightText>
                      {Array.isArray(config.roleConfigurations) && config.roleConfigurations.length > 0 ? (
                        <TableContainer component={Paper} sx={{ mt: 1, backgroundColor: '#081529' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    color: '#9AC0CD',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  Role Name
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#9AC0CD',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  Collection Name
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#9AC0CD',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  NFT Count
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#9AC0CD',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  Traits
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {config.roleConfigurations.map((roleConfig, rcIndex) => {
                                if (roleConfig.type === 'individual') {
                                  return roleConfig.requirements.map((req, reqIndex) => (
                                    <TableRow key={`${rcIndex}-${reqIndex}`}>
                                      {/* Role Name with RowSpan */}
                                      {reqIndex === 0 && (
                                        <TableCell rowSpan={roleConfig.requirements.length}>
                                          <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                            {roleConfig.roleName}
                                          </Typography>
                                        </TableCell>
                                      )}
                                      {/* Collection Name */}
                                      <TableCell>
                                        <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                          {req.collectionName}
                                        </Typography>
                                      </TableCell>
                                      {/* NFT Count */}
                                      <TableCell>
                                        <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                          {req.nftCount}
                                        </Typography>
                                      </TableCell>
                                      {/* Traits */}
                                      <TableCell>
                                        <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                          {Array.isArray(req.traits) && req.traits.length > 0
                                            ? req.traits.join(', ')
                                            : 'N/A'}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ));
                                } else if (roleConfig.type === 'combined') {
                                  const combinedCollections = roleConfig.combinedRequirement
                                    ? roleConfig.combinedRequirement.collectionAddresses
                                        .map((addr) =>
                                          availableCollections.find((col) => col.collectionAddress === addr)
                                            ?.name || addr
                                        )
                                        .join(', ')
                                    : 'N/A';
                                  const combinedCount = roleConfig.combinedRequirement
                                    ? roleConfig.combinedRequirement.combinedCount
                                    : 'N/A';
                                  return (
                                    <TableRow key={`combined-${rcIndex}`}>
                                      <TableCell>
                                        <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                          {roleConfig.roleName}
                                        </Typography>
                                      </TableCell>

                                      <TableCell>
                                        <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                          {combinedCollections}
                                        </Typography>
                                      </TableCell>

                                      <TableCell>
                                        <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                          {combinedCount}
                                        </Typography>
                                      </TableCell>

                                      <TableCell>
                                        <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                          N/A
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  );
                                } else {
                                  return null; // Handle unexpected types
                                }
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif', mt: 1 }}>
                          No role configurations available.
                        </Typography>
                      )}
                    </CardContent>
                    <StyledButtonBox>
                      <StyledButton
                        size="small"
                        onClick={() => handleEditConfig(config)}
                        colorType="edit"
                        startIcon={<EditIcon />}
                      >
                        Edit Configuration
                      </StyledButton>
                      <StyledButton
                        size="small"
                        onClick={() => handleDeleteConfig(config.guildId)}
                        colorType="delete"
                        startIcon={<DeleteIcon />}
                      >
                        Delete Configuration
                      </StyledButton>
                    </StyledButtonBox>
                  </StyledCard>
                </Grid>
              ))
            ) : (
              <Typography variant="h6" sx={{ color: '#C2CCDF', mt: 4, fontFamily: 'Inter, sans-serif' }}>
                No configurations available.
              </Typography>
            )}
          </Grid>
        </Box>
      </Box>

      {/* Edit Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        aria-labelledby="edit-config-modal-title"
        aria-describedby="edit-config-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 1000,
            bgcolor: '#040813',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            color: '#E0E4ED',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <Typography
            id="edit-config-modal-title"
            variant="h6"
            component="h2"
            sx={{
              mb: 2,
              color: '#A1D1F0',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Edit Configuration
          </Typography>
          {currentConfig && (
            <Box component="form" noValidate autoComplete="off">
              {/* Channel Selection */}
              <FormControl fullWidth sx={{ mb: 4 }}>
                <InputLabel
                  id="channel-select-label"
                  sx={{
                    color: '#E0E4ED',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Channel
                </InputLabel>
                <Select
                  labelId="channel-select-label"
                  value={editedConfig.channel || ''}
                  label="Channel"
                  onChange={(e) =>
                    setEditedConfig({ ...editedConfig, channel: e.target.value })
                  }
                  sx={{
                    color: '#E0E4ED', // Brand text color
                    backgroundColor: '#132A47', // Brand background color
                    fontFamily: 'Poppins, sans-serif', // Brand font
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: '#607080', // Brand border color
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#607080',
                    },
                    '.MuiSvgIcon-root ': {
                      fill: '#FFFFFF !important', // Dropdown icon color
                    },
                    borderRadius: '8px',
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#1A2027', // Brand dropdown background
                        color: '#FFFFFF', // Brand dropdown text
                        fontFamily: 'Poppins, sans-serif', // Brand font
                      },
                    },
                  }}
                >
                  {Array.isArray(channels) &&
                    channels.map((channel) => (
                      <MenuItem
                        key={channel.id}
                        value={channel.id}
                        sx={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {channel.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Role Configurations Section */}
              <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Poppins, sans-serif' }}>
                Role Configurations
              </Typography>
              <TableContainer component={Paper} sx={{ backgroundColor: '#081529' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          color: '#9AC0CD',
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 'bold',
                        }}
                      >
                        Role
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#9AC0CD',
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 'bold',
                        }}
                      >
                        Collection Name
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#9AC0CD',
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 'bold',
                        }}
                      >
                        NFT Count / Combined Count
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#9AC0CD',
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 'bold',
                        }}
                      >
                        Traits
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#9AC0CD',
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 'bold',
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(editedConfig.roleConfigurations) &&
                      editedConfig.roleConfigurations.map((roleConfig, rcIndex) =>
                        roleConfig.type === 'individual' ? (
                          Array.isArray(roleConfig.requirements) &&
                          roleConfig.requirements.map((req, reqIndex) => (
                            <TableRow key={`${rcIndex}-${reqIndex}`}>
                              {/* Role Dropdown with RowSpan */}
                              {reqIndex === 0 && (
                                <EditableTableCell rowSpan={roleConfig.requirements.length}>
                                  <FormControl fullWidth>
                                    <Select
                                      value={roleConfig.roleId}
                                      onChange={(e) =>
                                        handleRoleConfigChange(rcIndex, 'roleId', e.target.value)
                                      }
                                      displayEmpty
                                      sx={{
                                        color: '#E0E4ED', // Brand text color
                                        backgroundColor: '#132A47', // Brand background color
                                        fontFamily: 'Poppins, sans-serif', // Brand font
                                        '.MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#607080', // Brand border color
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#607080',
                                        },
                                        '.MuiSvgIcon-root ': {
                                          fill: '#FFFFFF !important', // Dropdown icon color
                                        },
                                        borderRadius: '4px',
                                      }}
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            backgroundColor: '#081529', // Brand dropdown background
                                            color: '#E0E4ED', // Brand dropdown text
                                            fontFamily: 'Poppins, sans-serif', // Brand font
                                          },
                                        },
                                      }}
                                    >
                                      <MenuItem value="" disabled>
                                        Select Role
                                      </MenuItem>
                                      {Array.isArray(roles) &&
                                        roles.map((role) => (
                                          <MenuItem
                                            key={role.id}
                                            value={role.id}
                                            sx={{ fontFamily: 'Poppins, sans-serif' }}
                                          >
                                            {role.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </EditableTableCell>
                              )}
                              {/* Collection Dropdown */}
                              <EditableTableCell>
                                <FormControl fullWidth>
                                  <Select
                                    value={req.collectionAddress}
                                    onChange={(e) =>
                                      handleRequirementChange(rcIndex, reqIndex, 'collectionAddress', e.target.value)
                                    }
                                    displayEmpty
                                    sx={{
                                      color: '#E0E4ED', // Brand text color
                                      backgroundColor: '#132A47', // Brand background color
                                      fontFamily: 'Poppins, sans-serif', // Brand font
                                      '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#607080', // Brand border color
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#607080',
                                      },
                                      '.MuiSvgIcon-root ': {
                                        fill: '#FFFFFF !important', // Dropdown icon color
                                      },
                                      borderRadius: '4px',
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          backgroundColor: '#1A2027', // Brand dropdown background
                                          color: '#FFFFFF', // Brand dropdown text
                                          fontFamily: 'Poppins, sans-serif', // Brand font
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      Select Collection
                                    </MenuItem>
                                    {Array.isArray(availableCollections) &&
                                      availableCollections.map((collection) => (
                                        <MenuItem
                                          key={collection.collectionAddress}
                                          value={collection.collectionAddress}
                                          sx={{ fontFamily: 'Poppins, sans-serif' }}
                                        >
                                          {collection.name}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                              </EditableTableCell>

                              {/* NFT Count or Combined Count */}
                              <EditableTableCell>
                                <TextField
                                  type="number"
                                  inputProps={{ min: 0 }}
                                  value={req.nftCount}
                                  onChange={(e) =>
                                    handleRequirementChange(rcIndex, reqIndex, 'nftCount', e.target.value)
                                  }
                                  sx={{
                                    input: { color: '#E0E4Ed', fontFamily: 'Poppins, sans-serif' },
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': {
                                        borderColor: '#607080',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#607080',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#607080',
                                      },
                                    },
                                    backgroundColor: '#132A47', // Brand background color
                                    borderRadius: '4px',
                                    width: '60px', // Shrinked width
                                    '& .MuiInputBase-input': {
                                      textAlign: 'center',
                                    },
                                  }}
                                />
                              </EditableTableCell>

                              {/* Traits Management */}
                              <EditableTableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {/* Display existing traits with delete option */}
                                  {Array.isArray(req.traits) &&
                                    req.traits.map((trait, traitIndex) => (
                                      <Box
                                        key={traitIndex}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          backgroundColor: '#132A47', // Brand background color
                                          padding: '6px 12px', // Increased padding
                                          borderRadius: '6px',
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ flexGrow: 1, color: '#ECF0F1', fontFamily: 'Poppins, sans-serif' }}>
                                          {trait}
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleDeleteTrait(rcIndex, reqIndex, traitIndex)}
                                          sx={{ color: '#E74C3C' }} // Brand error color
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    ))}

                                  {/* Input to add new trait */}
                                  {/* 
                                    If you want to allow adding traits within the Edit Modal in the future,
                                    you can uncomment the following TextField. 
                                    Currently, it's commented out to align with your requirement.
                                  */}
                                  {/* <TextField
                                    variant="outlined"
                                    placeholder="Add Trait and press Enter"
                                    onKeyPress={(e) => handleTraitInput(rcIndex, reqIndex, e)}
                                    sx={{
                                      input: { color: '#FFFFFF', fontFamily: 'Poppins, sans-serif' },
                                      '.MuiOutlinedInput-root': {
                                        '& fieldset': {
                                          borderColor: '#607080',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: '#607080',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#607080',
                                        },
                                      },
                                      backgroundColor: '#1A2027', // Brand background color
                                      borderRadius: '4px',
                                      width: '100%', // Full width
                                    }}
                                  /> */}
                                </Box>
                              </EditableTableCell>

                              {/* Actions: Remove Requirement */}
                              <EditableTableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => handleRemoveRequirement(rcIndex, reqIndex)}
                                  sx={{ color: '#E74C3C' }} // Brand error color
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </EditableTableCell>
                            </TableRow>
                          ))
                        ) : roleConfig.type === 'combined' ? (
                          <TableRow key={`combined-${rcIndex}`}>
                            {/* Role Dropdown */}
                            <EditableTableCell>
                              <FormControl fullWidth>
                                <Select
                                  value={roleConfig.roleId}
                                  onChange={(e) =>
                                    handleRoleConfigChange(rcIndex, 'roleId', e.target.value)
                                  }
                                  displayEmpty
                                  sx={{
                                    color: '#E0E4ED', // Brand text color
                                    backgroundColor: '#132A47', // Brand background color
                                    fontFamily: 'Poppins, sans-serif', // Brand font
                                    '.MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#607080', // Brand border color
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#607080',
                                    },
                                    '.MuiSvgIcon-root ': {
                                      fill: '#FFFFFF !important', // Dropdown icon color
                                    },
                                    borderRadius: '4px',
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: '#1A2027', // Brand dropdown background
                                        color: '#FFFFFF', // Brand dropdown text
                                        fontFamily: 'Poppins, sans-serif', // Brand font
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    Select Role
                                  </MenuItem>
                                  {Array.isArray(roles) &&
                                    roles.map((role) => (
                                      <MenuItem
                                        key={role.id}
                                        value={role.id}
                                        sx={{ fontFamily: 'Poppins, sans-serif' }}
                                      >
                                        {role.name}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </EditableTableCell>

                            {/* Combined Collections Multi-Select */}
                            <EditableTableCell>
                              <FormControl fullWidth>
                                <InputLabel
                                  id={`combined-collections-label-${rcIndex}`}
                                  sx={{
                                    color: '#E0E4ED',
                                    fontFamily: 'Poppins, sans-serif',
                                  }}
                                >
                                  Collections
                                </InputLabel>
                                <Select
                                  labelId={`combined-collections-label-${rcIndex}`}
                                  multiple
                                  value={roleConfig.combinedRequirement.collectionAddresses || []}
                                  onChange={(e) =>
                                    handleRequirementChange(rcIndex, null, 'collectionAddresses', e.target.value)
                                  }
                                  renderValue={(selected) =>
                                    selected
                                      .map(
                                        (addr) =>
                                          availableCollections.find((col) => col.collectionAddress === addr)?.name ||
                                          addr
                                      )
                                      .join(', ')
                                  }
                                  label="Collections"
                                  sx={{
                                    color: '#E0E4ED', // Brand text color
                                    backgroundColor: '#132A47', // Brand background color
                                    fontFamily: 'Poppins, sans-serif', // Brand font
                                    '.MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#607080', // Brand border color
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#607080',
                                    },
                                    '.MuiSvgIcon-root ': {
                                      fill: '#FFFFFF !important', // Dropdown icon color
                                    },
                                    borderRadius: '4px',
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: '#1A2027', // Brand dropdown background
                                        color: '#FFFFFF', // Brand dropdown text
                                        fontFamily: 'Poppins, sans-serif', // Brand font
                                      },
                                    },
                                  }}
                                >
                                  {Array.isArray(availableCollections) &&
                                    availableCollections.map((collection) => (
                                      <MenuItem key={collection.collectionAddress} value={collection.collectionAddress}>
                                        <Checkbox
                                          checked={
                                            roleConfig.combinedRequirement.collectionAddresses
                                              ? roleConfig.combinedRequirement.collectionAddresses.includes(collection.collectionAddress)
                                              : false
                                          }
                                        />
                                        <ListItemText primary={collection.name || 'Unknown Collection'} />
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </EditableTableCell>

                            {/* Combined NFT Count */}
                            <EditableTableCell>
                              <TextField
                                type="number"
                                inputProps={{ min: 0 }}
                                value={roleConfig.combinedRequirement.combinedCount || ''}
                                onChange={(e) =>
                                  handleRequirementChange(rcIndex, null, 'combinedCount', e.target.value)
                                }
                                sx={{
                                  input: { color: '#E0E4Ed', fontFamily: 'Poppins, sans-serif' },
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: '#607080',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#607080',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#607080',
                                    },
                                  },
                                  backgroundColor: '#132A47', // Brand background color
                                  borderRadius: '4px',
                                  width: '80px', // Adjusted width for combined count
                                  '& .MuiInputBase-input': {
                                    textAlign: 'center',
                                  },
                                }}
                              />
                            </EditableTableCell>

                            {/* Traits - Not Applicable */}
                            <EditableTableCell>
                              <Typography sx={{ color: '#E0E4ED', fontFamily: 'Inter, sans-serif' }}>
                                N/A
                              </Typography>
                            </EditableTableCell>

                            {/* Actions: Remove Role Configuration */}
                            <EditableTableCell>
                              <IconButton
                                color="error"
                                onClick={() => handleRemoveRoleConfig(rcIndex)}
                                sx={{ color: '#E74C3C' }} // Brand error color
                              >
                                <RemoveCircleOutlineIcon />
                              </IconButton>
                            </EditableTableCell>
                          </TableRow>
                        ) : null
                      )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* 
                Removed the "Add Role Configuration" button from the Edit Modal as per your requirement.
                If you decide to allow adding within the Edit Modal in the future, you can reintroduce it here.
              */}

              {/* Save and Cancel Buttons */}
              <Box mt={4} display="flex" justifyContent="flex-end">
                <Button
                  sx={{
                    color: '#A1D1F0',
                    marginRight: 2,
                    '&:hover': { backgroundColor: 'rgba(161, 209, 240, 0.1)' },
                    fontFamily: 'Poppins, sans-serif',
                  }}
                  onClick={handleCloseEditModal}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#728C63',
                    color: '#E0E4ED',
                    '&:hover': { backgroundColor: '#5F7079' },
                    fontFamily: 'Poppins, sans-serif',
                  }}
                  onClick={handleSaveEditConfig}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default CurrentConfigs;

